import { ClubLogo } from '../components/ClubLogo';
import { useId, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { de, roleLabel } from '@fsv/core';
import { DEMO_ROLES, sessionStore, signOut, startDemo, type DemoRole } from '../auth/sessionStore';
import { Avatar } from '../components/Avatar';
import { AvatarCropper } from '../components/AvatarCropper';
import { prepareAvatar } from '../lib/avatarImage';
import { errorMessage } from '../data/hooks';
import { demoHomePath } from '../lib/demoEntry';
import { Segmented, Switch } from '../components/Controls';
import { DamageRow } from '../components/DamageRow';
import { Icon } from '../components/Icon';
import { useToast } from '../components/Toast';
import { useBackend, useMyReports, useOptionalTenant, useSession, useTenantPath, useDamageRef } from '../data/hooks';
import { DEMO_EMAIL_CODE, tx, welcomePersona, type Me, type MyEmails } from '@fsv/data';
import { applyHighContrast, readHighContrast } from '../lib/contrastMode';
import { tenantPath } from '../lib/tenantRouting';
import { HelpLink, helpPath } from '../help/HelpLink';
import { useTour } from '../tour/TourProvider';
import { startTourWithDemoData } from '../tour/welcomeTour';
import { ErrorState, Loading } from './states';
import './ProfileScreen.css';

/**
 * The person's own picture (0014): pick an image, choose the square
 * (AvatarCropper), the browser crops and scales it (lib/avatarImage.ts), the
 * backend answers the new `me`.
 */
function ProfilePicture() {
  const { me } = useSession();
  const backend = useBackend();
  const toast = useToast();
  const [busy, setBusy] = useState(false);
  const [picked, setPicked] = useState<File | null>(null);
  const run = async (work: () => Promise<Me>) => {
    setBusy(true);
    try {
      sessionStore.setMe(await work());
    } catch (error) {
      toast.show(errorMessage(error), { tone: 'error' });
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="profile__picture">
      <Avatar person={{ id: me.memberId, name: me.displayName, avatarUrl: me.avatarUrl }} size={96} />
      <label className={`btn btn--ghost${busy ? ' is-busy' : ''}`}>
        <Icon name="camera" />
        {me.avatarUrl ? de.avatar.change : de.avatar.add}
        <input
          type="file"
          accept="image/*"
          className="sr-only"
          disabled={busy}
          onChange={(e) => {
            const file = e.target.files?.[0];
            e.target.value = '';
            if (file) setPicked(file);
          }}
        />
      </label>
      {me.avatarUrl && (
        <button type="button" className="btn btn--ghost" disabled={busy} onClick={() => void run(() => backend.removeAvatar())}>
          {de.avatar.remove}
        </button>
      )}
      {picked && (
        <AvatarCropper
          file={picked}
          onCancel={() => setPicked(null)}
          onApply={(crop) => {
            setPicked(null);
            void run(async () => backend.setAvatar(await prepareAvatar(picked, crop)));
          }}
        />
      )}
    </div>
  );
}

/**
 * „E-Mail-Adressen“ (0023): the first address, the further ones with a
 * remove button, and adding one — a code goes to that address, and only the
 * code entered here makes it count. Refusals (own address, limit, wrong code)
 * stay in the panel; the server says nothing about anybody else's address.
 */
function MyEmailsPanel() {
  const backend = useBackend();
  const session = useSession();
  const toast = useToast();
  const id = useId();
  const key = ['myEmails', backend.kind, session.me.memberId];
  const qc = useQueryClient();
  const emails = useQuery({ queryKey: key, queryFn: () => backend.myEmails() });
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [message, setMessage] = useState<{ tone: 'info' | 'error'; text: string } | null>(null);
  const [busy, setBusy] = useState(false);
  const t = tx.profile.emails;

  const run = async (work: () => Promise<MyEmails>, after?: string) => {
    setBusy(true);
    setMessage(null);
    try {
      qc.setQueryData(key, await work());
      if (after) toast.show(after);
      return true;
    } catch (error) {
      setMessage({ tone: 'error', text: errorMessage(error) });
      return false;
    } finally {
      setBusy(false);
    }
  };

  if (emails.isPending) return null;
  if (emails.isError) return null;
  const data = emails.data;
  return (
    <section className="panel" aria-labelledby={`${id}-h`} data-testid="my-emails">
      <h2 id={`${id}-h`}>{t.title}</h2>
      <p className="field__hint">{t.hint}</p>
      <p>
        <span className="mono">{data.primary}</span>
        <span className="muted"> · {t.primary}</span>
      </p>
      <p className="field__hint">{t.primaryHint}</p>
      <h3>{t.further}</h3>
      {data.additional.length === 0 ? (
        <p className="muted">{t.none}</p>
      ) : (
        <ul className="stack">
          {data.additional.map((a) => (
            <li key={a.id} className="row">
              <span className="mono">{a.email}</span>
              <span className="muted"> · {a.source === 'self' ? t.bySelf : t.byClub}</span>
              <span className="spacer" />
              <button
                type="button"
                className="btn btn--ghost"
                disabled={busy}
                aria-label={t.removeLabel(a.email)}
                onClick={() => {
                  if (!window.confirm(t.removeConfirm(a.email))) return;
                  void run(() => backend.removeMyEmail(a.id), tx.toast.emailRemoved);
                }}
              >
                {t.remove}
              </button>
            </li>
          ))}
        </ul>
      )}
      {data.pending ? (
        <div className="field">
          <p>{t.pending(data.pending.email)}</p>
          <p className="field__hint">{t.sent}</p>
          {session.mode === 'demo' && <p className="field__hint">{t.demoCode(DEMO_EMAIL_CODE)}</p>}
          <label className="field__label" htmlFor={`${id}-c`}>
            {t.codeLabel}
          </label>
          <div className="row">
            <input id={`${id}-c`} className="input" inputMode="numeric" autoComplete="one-time-code" value={code} onChange={(e) => setCode(e.target.value)} />
            <button
              type="button"
              className="btn btn--primary"
              disabled={busy || !code.trim()}
              onClick={async () => {
                if (await run(() => backend.confirmMyEmail(code), tx.toast.emailAdded)) setCode('');
              }}
            >
              {t.confirm}
            </button>
          </div>
        </div>
      ) : null}
      <div className="field">
        <label className="field__label" htmlFor={`${id}-e`}>
          {t.newLabel}
        </label>
        <div className="row">
          <input id={`${id}-e`} className="input" type="email" autoComplete="off" value={email} onChange={(e) => setEmail(e.target.value)} />
          <button
            type="button"
            className="btn"
            disabled={busy || !email.trim()}
            onClick={async () => {
              if (await run(() => backend.addMyEmail(email))) {
                setEmail('');
                setCode('');
              }
            }}
          >
            {t.send}
          </button>
        </div>
      </div>
      {message && (
        <div className={`notice${message.tone === 'error' ? ' notice--error' : ''}`} role="alert">
          <Icon name="alert" />
          <p>{message.text}</p>
        </div>
      )}
    </section>
  );
}

/** Profile; inside a tenant it also shows the role there, my reports and the admin link. */
export function ProfileScreen() {
  const session = useSession();
  const tenant = useOptionalTenant();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const backend = useBackend();
  const toast = useToast();
  const [contrast, setContrast] = useState(readHighContrast);
  const tour = useTour();
  const { me } = session;

  const leave = async (scope: 'this' | 'all') => {
    await signOut(scope);
    qc.clear();
    navigate('/login', { replace: true });
  };

  return (
    <div className="page page--narrow profile">
      <header className="profile__card">
        <ProfilePicture />
        <div className="profile__who">
          <h1>{me.displayName}</h1>
          <p className="muted">{me.email}</p>
          <p className="row">
            {tenant && <span className="tag" title={tx.profile.roleIn(tenant.name)}>{roleLabel[tenant.role]}</span>}
            {me.isPlatformManager && <span className="tag">{tx.profile.platform}</span>}
            <span className="tag">{session.mode === 'demo' ? tx.profile.modeDemo : tx.profile.modeApi}</span>
          </p>
        </div>
      </header>

      {me.memberships.length > 0 && (
        <section className="panel" aria-labelledby="clubs-h">
          <h2 id="clubs-h">{tx.profile.memberships}</h2>
          <ul className="profile__clubs">
            {me.memberships.map((m) => (
              <li key={m.tenantId}>
                <Link to={tenantPath(m.slug)} className="profile__link" aria-current={m.slug === tenant?.slug ? 'true' : undefined}>
                  <ClubLogo club={m} size={24} fallback={<Icon name="club" />} />
                  <span className="profile__club">
                    {m.name}
                    <span className="muted"> · {roleLabel[m.role]}</span>
                  </span>
                  <Icon name="forward" />
                </Link>
              </li>
            ))}
          </ul>
        </section>
      )}

      <MyEmailsPanel />

      {session.mode === 'demo' && (
        <section className="panel profile__demo" aria-labelledby="demo-h">
          <h2 id="demo-h">{tx.profile.demoRole}</h2>
          <p className="field__hint">{tx.app.demoBanner}</p>
          <Segmented
            label={tx.profile.demoRole}
            size="l"
            value={session.demoRole ?? 'member'}
            onChange={(r: DemoRole) => {
              startDemo(r);
              void qc.resetQueries();
              // The new persona may not be a member of the current club; every persona is one of the demo club.
              if (tenant) navigate(demoHomePath(), { replace: true });
            }}
            options={DEMO_ROLES.map((r) => ({ value: r, label: roleLabel[r] }))}
          />
          {tour.available && (
            <button type="button" className="btn" onClick={tour.start}>
              <Icon name="flag" />
              {tx.tour.restart}
            </button>
          )}
          <button
            type="button"
            className="btn"
            onClick={async () => {
              if (!window.confirm(tx.profile.demoResetConfirm)) return;
              await backend.reset?.();
              await qc.resetQueries();
              toast.show(tx.toast.demoReset);
            }}
          >
            <Icon name="refresh" />
            {tx.profile.demoReset}
          </button>
        </section>
      )}

      <nav className="panel profile__links" aria-label={tx.help.title}>
        <Link to={helpPath(tenant?.slug)} className="profile__link">
          <Icon name="help" />
          {tx.help.title}
          <Icon name="forward" />
        </Link>
        {session.mode === 'api' && (
          <button
            type="button"
            className="profile__link"
            data-testid="profile-welcome-tour"
            onClick={() => {
              const path = startTourWithDemoData(welcomePersona(me.memberships.map((m) => m.role)));
              qc.clear();
              navigate(path, { replace: true });
            }}
          >
            <Icon name="flag" />
            {de.welcome.restart}
            <Icon name="forward" />
          </button>
        )}
      </nav>

      {(tenant || me.isPlatformManager) && (
        <nav className="panel profile__links" aria-label={tx.profile.title}>
          {tenant && (
            <Link to={tenantPath(tenant.slug, '/profil/meldungen')} className="profile__link">
              <Icon name="upload" />
              {tx.profile.myReports}
              <Icon name="forward" />
            </Link>
          )}
          {tenant?.role === 'admin' && (
            <Link to={tenantPath(tenant.slug, '/admin')} className="profile__link">
              <Icon name="admin" />
              {tx.profile.admin}
              <Icon name="forward" />
            </Link>
          )}
          {me.isPlatformManager && (
            <Link to="/plattform" className="profile__link">
              <Icon name="platform" />
              {tx.profile.platform}
              <Icon name="forward" />
            </Link>
          )}
        </nav>
      )}

      <section className="panel" aria-labelledby="display-h">
        <h2 id="display-h">{tx.profile.display}</h2>
        <Switch
          checked={contrast}
          label={tx.profile.highContrast}
          hint={tx.profile.highContrastHint}
          onChange={(on) => {
            setContrast(on);
            applyHighContrast(on);
          }}
        />
      </section>

      <section className="panel">
        <div className="row">
          <button type="button" className="btn btn--ink" onClick={() => void leave('this')}>
            <Icon name="logout" />
            {de.auth.signOut}
          </button>
          {session.mode === 'api' && (
            <button
              type="button"
              className="btn btn--danger"
              onClick={() => {
                if (!window.confirm(`${de.auth.signOutEverywhereConfirm}\n\n${de.auth.signOutEverywhereConfirmBody}`)) return;
                void leave('all');
              }}
            >
              {de.auth.signOutEverywhere}
            </button>
          )}
        </div>
        {session.mode === 'api' && <p className="field__hint">{de.auth.signOutEverywhereHint}</p>}
      </section>
    </div>
  );
}

export function MyReportsScreen() {
  const ref = useDamageRef();
  const mine = useMyReports();
  const to = useTenantPath();
  return (
    <div className="page page--narrow">
      <Link to={to('/profil')} className="btn btn--ghost profile__back">
        <Icon name="back" />
        {tx.profile.title}
      </Link>
      <div className="row">
        <h1>{tx.profile.myReports}</h1>
        <span className="spacer" />
        <HelpLink screen="myReports" />
      </div>
      {mine.isPending ? (
        <Loading />
      ) : mine.isError ? (
        <ErrorState error={mine.error} />
      ) : mine.data.length === 0 ? (
        <p className="empty">{tx.profile.myReportsEmpty}</p>
      ) : (
        <div className="stack">
          {mine.data.map((d) => (
            <DamageRow
              key={d.id}
              damage={d}
              extra={
                d.reviewNote || d.mergedIntoId ? (
                  <span className="profile__note">
                    {d.reviewNote}
                    {d.mergedIntoNumber !== null && <span className="mono"> → {ref(d.mergedIntoNumber)}</span>}
                  </span>
                ) : undefined
              }
            />
          ))}
        </div>
      )}
    </div>
  );
}
